import { useCallback, useMemo, useRef, useState } from 'react'
import { IMoreMenuItem, toast } from '@sales-center/components'
import { useDialogState } from '@user/utils/useDialogState'
import { SmallLocationCardProps } from '../SmallLocationCard'
import {
  hasActiveAppointementMessage,
  isAllServicesAssignedToLocationMessage,
  lastLocationService,
  permissionDenied,
  useCheckPermissions,
} from '@user/store/authorization/permissions'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { deleteEntityLocation } from '@user/store/entityLocations'
import { isAPIValidationError } from '@user/store/services/crud'
import { ENTITY_TYPES } from '@sales-center/common'
import {
  decrementLeadServicesCount,
  getLeadDetail,
  getLeadValue,
} from '@user/store/lead'
import {
  decrementOppServicesCount,
  getOpportunityDetail,
  getOpportunityValue,
} from '@user/store/opportunity'
import { useAppSelector } from '@user/store/hooks'
import { selectLeadDetail } from '@user/store/lead/slice'
import { selectOpportunityDetail } from '@user/store/opportunity/slice'
import { selectAllEntityAppointments } from '@user/store/scheduling'

export function useDeleteDialog(
  props: SmallLocationCardProps,
  matched: boolean,
) {
  const {
    entityId,
    entityType,
    location: { id, externalLinkId },
  } = props

  const { lastServiceLocation = false } = props.isEditable ? props : {}
  const { isAllServicesAssignedToLocation = false } = props.isEditable
    ? props
    : {}

  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const [canDeleteLeads, canDeleteOpps] = useCheckPermissions(
    'leads_delete',
    'opportunities_delete',
  )

  const allAppts = useAppSelector(selectAllEntityAppointments)

  const hasActiveAppointment = useMemo(
    () =>
      allAppts.some(
        ({ status, locationId }) =>
          status !== 'Cancelled' &&
          locationId !== null &&
          locationId == externalLinkId,
      ),
    [allAppts, externalLinkId],
  )

  const dispatch = useAppDispatch()

  const canDeleteEntity =
    entityType === ENTITY_TYPES.Lead ? !!canDeleteLeads : !!canDeleteOpps

  const [open, onOpen, onClose] = useDialogState()

  // Guards against multiple clicks of the Remove/Delete button while the
  // delete request is in flight. The ref blocks same-tick double-clicks
  // synchronously; the state disables the button for visual feedback.
  const isDeletingRef = useRef(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const deleteMenuOption: IMoreMenuItem = useMemo(
    () => ({
      label: matched ? 'Remove' : 'Delete',
      onClick: onOpen,
      disabled:
        !canDeleteEntity ||
        lastServiceLocation ||
        hasActiveAppointment ||
        (isAllServicesAssignedToLocation &&
          entityType === ENTITY_TYPES.Opportunity),
      tooltipText:
        !canDeleteEntity || lastServiceLocation || hasActiveAppointment
          ? hasActiveAppointment
            ? hasActiveAppointementMessage
            : lastServiceLocation
              ? lastLocationService
              : permissionDenied
          : isAllServicesAssignedToLocation &&
              entityType === ENTITY_TYPES.Opportunity
            ? isAllServicesAssignedToLocationMessage
            : undefined,
    }),
    [
      canDeleteEntity,
      lastServiceLocation,
      matched,
      onOpen,
      hasActiveAppointment,
      isAllServicesAssignedToLocation,
    ],
  )

  const okClick = useCallback(async () => {
    if (isDeletingRef.current) return
    isDeletingRef.current = true
    setIsDeleting(true)
    try {
      const result = await dispatch(
        deleteEntityLocation({ id, entityType, entityId }),
      )

      if (deleteEntityLocation.fulfilled.match(result)) {
        const deletedServicesCount = result.payload.deleteProductsCount
        if (entityType === ENTITY_TYPES.Lead) {
          dispatch(
            decrementLeadServicesCount({
              leadId: entityId,
              count: deletedServicesCount,
            }),
          )
          if (detail?.id == entityId) dispatch(getLeadDetail(entityId))
          dispatch(getLeadValue(entityId))
        } else {
          dispatch(
            decrementOppServicesCount({
              oppId: entityId,
              count: deletedServicesCount,
            }),
          )
          if (detail?.id == entityId) dispatch(getOpportunityDetail(entityId))
          dispatch(getOpportunityValue(entityId))
        }
        onClose()
      }

      if (deleteEntityLocation.rejected.match(result)) {
        const { payload } = result
        if (payload) {
          if (isAPIValidationError(payload)) toast.error(payload.title)
          else toast.error(payload.Message)
        } else toast.error('Unable to delete Location')
        onClose()
      }
    } catch {
      toast.error('Unable to delete Location')
      onClose()
    } finally {
      isDeletingRef.current = false
      setIsDeleting(false)
    }
  }, [detail?.id, dispatch, entityId, entityType, id, onClose])

  return {
    deleteMenuOption,
    deleteDialogProps: {
      open,
      cancelClick: onClose,
      okClick,
      okButtonDisabled: isDeleting,
    },
  }
}
